/**
 * HUD scale (#755) — the wrappers that APPLY the one "how big is the in-match
 * HUD" knob. The knob itself ({@link HUD_SCALE}) now lives in
 * `render/hudLayout.ts` and is re-exported here (see below).
 *
 * Precedent: `render/projection.ts`'s `DEFAULT_ZOOM` (the one documented,
 * deliberately-re-tunable camera constant). This is its HUD-chrome sibling:
 * a single exported number that uniformly scales the screen-anchored combat
 * HUD (ability icons, unit frames, cast bars, aura chips) without touching
 * any of the ~120 px literals spread across those components. The next
 * "make the HUD bigger/smaller" request is a one-line edit in
 * `render/hudLayout.ts`.
 *
 * PURELY COSMETIC / CLIENT-SIDE presentation. Nothing crosses the wire, no
 * sim state, no hash — no `SIM_VERSION` bump, no golden regen.
 *
 * ## How the scaling is applied (and why it is NOT one wrapper)
 *
 * The active-phase HUD components are independently absolutely-positioned
 * siblings pinned to DIFFERENT screen edges. A single wrapper with a single
 * `transform-origin` would scale edge-anchored elements AWAY from their
 * edge — the action bar drifting up off the bottom, the frames pulling in
 * from the top — which reads as "everything floated toward the middle"
 * instead of "same layout, bigger". So each anchor region gets its OWN
 * wrapper whose `transform-origin` is the edge its contents are pinned to
 * ({@link HudScaleRegion}).
 *
 * Regions, derived from the components' own `position`/`top`/`bottom`
 * styles (see `MatchScreen`):
 *   • "top"    — `FrameCluster` (`top: 16`, full width) and `SelfAuraBar`
 *                (`top: 16, right: 16`). Both pinned to the TOP edge.
 *   • "bottom" — `UnitFrameStack` (`bottom: 12`, full width), `CastBar`
 *                (`bottom: 96`, centered) and `ActionBar` (`bottom: 32`,
 *                centered). All pinned to the BOTTOM edge.
 *
 * ## Why the wrapper is counter-sized
 *
 * A `scale()` on a full-viewport box grows the box past the viewport, so a
 * child pinned to the edge OPPOSITE the transform origin walks off-screen
 * (the top/bottom regions both contain a full-width `left: 0; right: 0`
 * flex row whose two groups are pinned to BOTH side edges — with a centered
 * origin each group would slide `(HUD_SCALE - 1) / 2` of the viewport width
 * outward and clip). Sizing the wrapper to `100% / HUD_SCALE` and pinning
 * it at its anchor makes the SCALED box land exactly on the viewport
 * rectangle again: every child keeps its edge anchoring, and every px inset
 * inside the region is uniformly multiplied by `HUD_SCALE`. That is exactly
 * "zoom in on the HUD".
 *
 * ## Deliberately outside these wrappers
 *
 *   • `NameplateAuras` — WORLD-anchored, not screen-anchored: it positions
 *     each chip row at `left: project(e.x, e.y).sx`. Scaling the wrapper
 *     would multiply those projected pixel coordinates too and every row
 *     would drift off its unit, worsening with distance from the origin.
 *     It stays a bare sibling and is not touched by #755 at all.
 *   • `InputSurface` — the full-viewport world input surface. Its
 *     coordinates feed world hit-testing and the `aimPoint` wire stream;
 *     scaling it would corrupt targeting and facing.
 *
 * ## Interaction with the camera's reserved strip (#679 → #781, RESOLVED)
 *
 * The follow camera reserves a bottom strip so a champion is never clamped
 * behind the action bar. #755 left that reserve stale: a flat `100` px
 * modelling `bottom: 32` + a 56px slot, i.e. ~6px SHORT once `HUD_SCALE`
 * became 1.2 (`(32 + 56) · 1.2 = 105.6`). #781 fixed it by scaling the
 * reserve with viewport height under a floor above that real footprint —
 * and by moving the knob itself into `render/hudLayout.ts`, which derives
 * the footprint from it. That is why {@link HUD_SCALE} is imported and
 * re-exported below instead of declared here: `render/` must not import
 * from `ui/`, so the shared number lives in `render/` and flows upward.
 * Every existing `import { HUD_SCALE } from "./hudScale.js"` keeps working.
 *
 * ## Pointer events
 *
 * A region wrapper spans the whole viewport, so it MUST be
 * `pointerEvents: "none"` or it would swallow every world click destined
 * for `InputSurface` underneath. `pointer-events` inherits, so interactive
 * HUD content re-enables it on its own root (the unit frames and aura chips
 * already did; `ActionBar` sets it explicitly for #755). Hit areas
 * themselves need no correction: a CSS transform scales an element's hit
 * box together with its visual box.
 */

import type { CSSProperties, ReactElement, ReactNode } from "react";
import { HUD_SCALE } from "../render/hudLayout.js";

/** THE HUD-size knob, re-exported from its canonical home
 *  `render/hudLayout.ts` (#781 — the camera's reserved bottom strip is
 *  derived from it and `render/` cannot import from `ui/`). Change the value
 *  there; keep importing it from either module. */
export { HUD_SCALE };

/** Screen edge a HUD region is pinned to; one scaled wrapper per value. */
export type HudAnchor = "top" | "bottom";

/**
 * Wrapper style for one anchor region: counter-sized to `100% / HUD_SCALE`
 * and pinned at its anchor so the SCALED box maps exactly back onto the
 * viewport (see the module header). Exported for tests/assertions.
 */
export function hudScaleRegionStyle(anchor: HudAnchor): CSSProperties {
  const counterSized = `calc(100% / ${String(HUD_SCALE)})`;
  const base = {
    position: "absolute",
    left: 0,
    width: counterSized,
    height: counterSized,
    transform: `scale(${String(HUD_SCALE)})`,
    // Full-viewport box over the world input surface — never capture
    // pointer events; interactive children re-enable `auto` themselves.
    pointerEvents: "none",
  } as const satisfies CSSProperties;
  return anchor === "top"
    ? { ...base, top: 0, transformOrigin: "top left" }
    : { ...base, bottom: 0, transformOrigin: "bottom left" };
}

export interface HudScaleRegionProps {
  readonly anchor: HudAnchor;
  readonly children: ReactNode;
}

/**
 * One `HUD_SCALE` wrapper for one anchor region. Any HUD component placed
 * inside inherits the scale automatically — which is the point: a future
 * active-phase HUD element needs no per-component sizing work.
 *
 * data-* contract:
 *   • data-testid="hud-scale-top" / "hud-scale-bottom"
 *   • data-hud-scale — the applied multiplier (structural assertions read
 *     this instead of re-deriving a post-scale magic number)
 */
export function HudScaleRegion({ anchor, children }: HudScaleRegionProps): ReactElement {
  return (
    <div
      data-testid={`hud-scale-${anchor}`}
      data-hud-scale={String(HUD_SCALE)}
      style={hudScaleRegionStyle(anchor)}
    >
      {children}
    </div>
  );
}
